/**
 * Bill-level discount entry modal (F8).
 *
 * Reuses the `DiscountInput` atom from @bbpos/ui for the amount / % toggle and
 * value entry, adds a mandatory reason textarea, a role-based cap check,
 * and a live preview strip so the cashier can see the final total before
 * applying. The parent wires the resulting discount into the cart store
 * via `applyBillDiscount`; pro-ration across lines happens in
 * `useBillCalculations` so per-line GST stays floor-correct.
 *
 * FR-BILL-050: When the requested discount exceeds the caller's role cap,
 * the "Apply" button becomes "Request Approval" (amber). The modal also
 * renders a read-only "Awaiting approval…" state when a pending approval
 * exists, and surfaces approve/reject results via props.
 *
 * Empty-state "Remove discount" only shows when an existing discount is
 * being edited.
 */

import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, DiscountInput, Input, TextArea } from '@bbpos/ui';
// Lazy so the heavy camera stack (html5-qrcode) only loads when the cashier
// actually taps "Scan" — keeps the modal's initial import light.
const CameraBarcodeScanner = lazy(() =>
    import('./CameraBarcodeScanner').then((m) => ({ default: m.CameraBarcodeScanner })),
);
import type { DiscountType as AtomDiscountType } from '@bbpos/ui/src/components/POSBillingComponents/DiscountInput/types';
import { GLASS, cn } from '@/constants/design-tokens';
import { formatCurrency } from '@/lib/format';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import { getRolePermission, normalizeRole } from '@/lib/discount-caps';
import { computeUnprofitableLines } from '@/lib/margin-check';
import type { BillDiscount, CartLineItem, PendingDiscountApproval } from '@/stores/use-cart-store';

// ─── Props ───────────────────────────────────────────────────────────────────

export interface BillDiscountModalProps {
    open: boolean;
    /** Pre-bill-discount taxable base (paise) — used for preview + clamping. */
    currentSubtotal: number;
    /** Existing bill discount to edit; null for fresh entry. */
    currentDiscount: BillDiscount | null;
    /** Caller's role (from auth store) — drives the role-based cap. */
    callerRole: string;
    /** Tenant currency. Optional; defaults to INR so the existing caller
     *  (BillingPage) compiles unchanged and INR output stays byte-identical,
     *  while non-INR tenants render the correct symbol/decimals. */
    currency?: CurrencyCode;
    /** FR-BILL-043: Line items for margin/profitability check. When provided,
     *  the modal shows a warning if any line would become unprofitable. */
    lineItems?: Pick<CartLineItem, 'name' | 'sku' | 'unitCost' | 'quantity' | 'lineSubtotal' | 'discountAmount'>[];
    /** FR-BILL-050: pending discount approval in flight, if any. */
    pendingApproval: PendingDiscountApproval | null;
    onApply: (discount: Omit<BillDiscount, 'appliedAt' | 'appliedBy'>) => void;
    /** FR-BILL-050: called when the cashier requests approval for an over-cap discount. */
    onRequestApproval?: (dto: { type: 'PERCENTAGE' | 'FLAT'; value: number; reason: string }) => Promise<void>;
    /**
     * Voucher validation hook. When provided, the modal exposes a voucher
     * code input. On success the modal prefills the discount type/value
     * from the voucher and tags the resulting BillDiscount with `voucherCode`
     * so checkout can call /vouchers/:code/redeem (which tracks used_count).
     */
    onValidateVoucher?: (code: string) => Promise<{
        type: 'PERCENTAGE' | 'FLAT';
        discount_amount: number;
        message: string;
    }>;
    onClear: () => void;
    onClose: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function BillDiscountModal({
    open,
    currentSubtotal,
    currentDiscount,
    callerRole,
    currency = 'INR',
    lineItems,
    pendingApproval,
    onApply,
    onRequestApproval,
    onValidateVoucher,
    onClear,
    onClose,
}: BillDiscountModalProps) {
    const normalizedRole = useMemo(() => normalizeRole(callerRole), [callerRole]);
    // Not memoised on callerRole: the served cap lands after a reload while the
    // role is already known, so a memo would keep the shipped default (SS3-007).
    const permission = getRolePermission(callerRole);
    const cap = permission.kind === 'allowed' ? permission.cap : { percent: 0, flat: 0 };
    const roleIsPermitted = permission.kind === 'allowed';

    // Currency-bound money formatter — delegates to the shared, subunit-aware
    // `@/lib/format#formatCurrency`. INR (the default) stays byte-identical.
    const fmt = useMemo(
        () => (paise: number) => formatCurrency(paise, currency),
        [currency],
    );

    // Internal form state. DiscountInput uses the lowercase union
    // ('flat' | 'percent'); we translate at the boundary to the store's
    // uppercase ('FLAT' | 'PERCENTAGE').
    const [atomType, setAtomType] = useState<AtomDiscountType>(
        currentDiscount?.type === 'PERCENTAGE' ? 'percent' : 'flat',
    );
    // `rawValue` mirrors the live input. `committedValue` is what drives
    // the preview / validation / submission — it only updates when the
    // discount type toggle is changed or the inline Apply button is
    // clicked. This decouples the calculation from every keystroke.
    // The store holds FLAT in the minor unit (paise) while the form works in
    // major units (rupees) — convert on the way in, mirroring the voucher
    // prefill below. PERCENTAGE needs no conversion.
    const toMajor = (d: BillDiscount | null): number =>
        !d ? 0 : d.type === 'PERCENTAGE' ? d.value : d.value / getSubunitMultiplier(currency);
    const [rawValue, setRawValue] = useState<number>(toMajor(currentDiscount));
    const [committedValue, setCommittedValue] = useState<number>(toMajor(currentDiscount));
    const [reason, setReason] = useState<string>(currentDiscount?.reason ?? '');
    const [isRequesting, setIsRequesting] = useState(false);
    const [voucherCode, setVoucherCode] = useState<string>(currentDiscount?.voucherCode ?? '');
    const [voucherStatus, setVoucherStatus] = useState<
        | { kind: 'idle' }
        | { kind: 'validating' }
        // `amount` is the voucher's discount in the minor unit, as validated.
        | { kind: 'valid'; code: string; message: string; amount: number }
        | { kind: 'error'; message: string }
    >({ kind: 'idle' });
    // Camera scan overlay for reading a voucher QR/barcode into the code field.
    const [scannerOpen, setScannerOpen] = useState(false);
    const panelRef = useRef<HTMLDivElement | null>(null);

    // Reset form when the modal is opened with a different discount (or
    // opened fresh). Guarded by `open` so mounted-but-closed transitions
    // don't clobber state.
    useEffect(() => {
        if (!open) return;
        setAtomType(currentDiscount?.type === 'PERCENTAGE' ? 'percent' : 'flat');
        setRawValue(toMajor(currentDiscount));
        setCommittedValue(toMajor(currentDiscount));
        setReason(currentDiscount?.reason ?? '');
        setIsRequesting(false);
        setScannerOpen(false);
        setVoucherCode(currentDiscount?.voucherCode ?? '');
        setVoucherStatus(currentDiscount?.voucherCode
            ? {
                kind: 'valid',
                code: currentDiscount.voucherCode,
                message: `Voucher ${currentDiscount.voucherCode} applied`,
                amount: currentDiscount.value,
            }
            : { kind: 'idle' },
        );
    }, [open, currentDiscount]);

    // Focus management — when the modal opens on a permitted role, move
    // focus to the first interactive control (the DiscountInput amount
    // field). Keeps F8 → start typing flowing cleanly for cashiers.
    useEffect(() => {
        if (!open || !roleIsPermitted || pendingApproval) return;
        // Run after the slide-up animation settles so the element is laid out.
        const t = setTimeout(() => {
            const input = panelRef.current?.querySelector<HTMLInputElement>(
                'input[type="number"], input[type="text"], input[inputmode]',
            );
            input?.focus();
            input?.select?.();
        }, 50);
        return () => clearTimeout(t);
    }, [open, roleIsPermitted, pendingApproval]);

    // Escape closes
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                onClose();
            }
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [open, onClose]);

    // ── Derived validation ────────────────────────────────────────────────
    const isPercent = atomType === 'percent';
    const storeType: BillDiscount['type'] = isPercent ? 'PERCENTAGE' : 'FLAT';
    // The DiscountInput atom reports value in whole major units for FLAT mode
    // (because it uses a user-facing currency-amount input). Convert to the
    // minor unit using the tenant's currency subunit factor (×100 for INR /
    // AED / SAR / QAR, ×1000 for OMR / BHD / KWD) before handing it to the
    // store / cap check. Driven by `committedValue` so that the preview only
    // reflects values the cashier has explicitly confirmed (via inline Apply
    // or a type toggle).
    const subunitMultiplier = useMemo(() => getSubunitMultiplier(currency), [currency]);
    const valueInStoreUnits = isPercent
        ? committedValue
        : Math.round(committedValue * subunitMultiplier);

    // A validated voucher was authorised when it was issued, so its own
    // amount is not a manual discount and the role cap does not apply.
    // Any other value (the cashier edited it) is checked like a manual one.
    const isVoucherAmount = (type: BillDiscount['type'], value: number) =>
        voucherStatus.kind === 'valid' && type === 'FLAT' && value === voucherStatus.amount;

    const overCap = isVoucherAmount(storeType, valueInStoreUnits)
        ? false
        : isPercent
            ? valueInStoreUnits > cap.percent
            : cap.flat !== null && valueInStoreUnits > cap.flat;

    const reasonTrimmed = reason.trim();
    const reasonValid = reasonTrimmed.length >= 3 && reasonTrimmed.length <= 500;
    const valueValid = valueInStoreUnits > 0 && (!isPercent || valueInStoreUnits <= 100);

    const canApply = roleIsPermitted && valueValid && reasonValid && !overCap;
    const canRequestApproval = roleIsPermitted && valueValid && reasonValid && overCap && !!onRequestApproval;

    // ── Preview ───────────────────────────────────────────────────────────
    const previewDiscountAmount = useMemo(() => {
        if (!valueValid || currentSubtotal <= 0) return 0;
        if (isPercent) {
            return Math.floor((currentSubtotal * valueInStoreUnits) / 100);
        }
        return Math.min(valueInStoreUnits, currentSubtotal);
    }, [valueValid, isPercent, valueInStoreUnits, currentSubtotal]);

    const previewFinal = Math.max(0, currentSubtotal - previewDiscountAmount);

    // ── FR-BILL-043: P&L unprofitability check ───────────────────────────
    const unprofitableLines = useMemo(() => {
        if (!valueValid || !lineItems || lineItems.length === 0) return [];
        return computeUnprofitableLines(lineItems, storeType, valueInStoreUnits);
    }, [valueValid, lineItems, storeType, valueInStoreUnits]);

    // ── Preview for pending approval ──────────────────────────────────────
    const pendingPreviewAmount = useMemo(() => {
        if (!pendingApproval || currentSubtotal <= 0) return 0;
        if (pendingApproval.type === 'PERCENTAGE') {
            return Math.floor((currentSubtotal * pendingApproval.value) / 100);
        }
        return Math.min(pendingApproval.value, currentSubtotal);
    }, [pendingApproval, currentSubtotal]);

    // ── Handlers ──────────────────────────────────────────────────────────
    // Resolve the current submit value from `rawValue` (latest typed input)
    // rather than `committedValue` so that the footer Apply works even when
    // the cashier hasn't tapped the inline Apply first. The inline Apply
    // remains a UX affordance for refreshing the preview mid-entry.
    const submitValue = isPercent
        ? Math.min(rawValue, 100)
        : Math.round(rawValue * subunitMultiplier);
    const submitValueValid = submitValue > 0 && (!isPercent || submitValue <= 100);
    const submitOverCap = isVoucherAmount(storeType, submitValue)
        ? false
        : isPercent
            ? submitValue > cap.percent
            : cap.flat !== null && submitValue > cap.flat;
    const canSubmit = roleIsPermitted && submitValueValid && reasonValid && !submitOverCap;
    const canSubmitRequestApproval =
        roleIsPermitted && submitValueValid && reasonValid && submitOverCap && !!onRequestApproval;

    const handleApply = () => {
        if (!canSubmit) return;
        // Commit so the preview reflects exactly what was submitted.
        setCommittedValue(isPercent ? Math.min(rawValue, 100) : rawValue);
        const validatedVoucher = voucherStatus.kind === 'valid' ? voucherStatus.code : undefined;
        onApply({
            type: storeType,
            value: submitValue,
            reason: reasonTrimmed,
            ...(validatedVoucher ? { voucherCode: validatedVoucher } : {}),
        });
    };

    const handleValidateVoucher = async (codeArg?: string) => {
        const code = (codeArg ?? voucherCode).trim().toUpperCase();
        if (!code || !onValidateVoucher) return;
        setVoucherStatus({ kind: 'validating' });
        try {
            const result = await onValidateVoucher(code);
            // Prefill discount form from the validated voucher. We always
            // load it as FLAT (paise) because the server-side redeem path
            // will re-compute against the live bill subtotal anyway.
            setAtomType('flat');
            const major = result.discount_amount / subunitMultiplier;
            setRawValue(major);
            setCommittedValue(major);
            setReason(`Voucher: ${code}`);
            setVoucherStatus({ kind: 'valid', code, message: result.message, amount: result.discount_amount });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Voucher could not be applied';
            setVoucherStatus({ kind: 'error', message });
        }
    };

    const handleClearVoucher = () => {
        setVoucherCode('');
        setVoucherStatus({ kind: 'idle' });
        setRawValue(0);
        setCommittedValue(0);
        setReason('');
    };

    // Scanned a voucher QR/barcode — fill the code and validate it immediately.
    const handleScanVoucher = (raw: string) => {
        const code = raw.trim().toUpperCase();
        setScannerOpen(false);
        if (!code) return;
        setVoucherCode(code);
        setVoucherStatus({ kind: 'idle' });
        void handleValidateVoucher(code);
    };

    const handleRequestApproval = async () => {
        if (!canSubmitRequestApproval || isRequesting) return;
        setIsRequesting(true);
        try {
            setCommittedValue(isPercent ? Math.min(rawValue, 100) : rawValue);
            await onRequestApproval!({
                type: storeType,
                value: submitValue,
                reason: reasonTrimmed,
            });
        } catch {
            // Error toast handled by the parent
        } finally {
            setIsRequesting(false);
        }
    };

    if (!open) return null;

    const capLabel = isPercent
        ? `${cap.percent}%`
        : cap.flat === null ? null : fmt(cap.flat);
    const confirmAriaLabel = canApply
        ? `Apply ${isPercent ? `${valueInStoreUnits}%` : fmt(valueInStoreUnits)} bill discount, reason: ${reasonTrimmed}`
        : canRequestApproval
            ? `Request approval for ${isPercent ? `${valueInStoreUnits}%` : fmt(valueInStoreUnits)} discount`
            : 'Apply bill discount';

    // ── Pending Approval State ────────────────────────────────────────────
    if (pendingApproval) {
        const pendingLabel = pendingApproval.type === 'PERCENTAGE'
            ? `${pendingApproval.value}%`
            : fmt(pendingApproval.value);

        return (
            <div
                className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in p-0 sm:p-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="bill-discount-title"
            >
                <div
                    ref={panelRef}
                    className={cn(
                        GLASS,
                        'w-full sm:w-[560px] max-h-[92vh] flex flex-col animate-slide-up',
                    )}
                >
                    {/* ── Header ─────────────────────────────────────── */}
                    <div className="px-5 py-4 flex items-start justify-between">
                        <div>
                            <h2
                                id="bill-discount-title"
                                className="text-lg font-semibold text-warning-700 dark:text-warning-400"
                            >
                                Discount Pending Approval
                            </h2>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                                Waiting for a Manager or Owner to approve
                            </p>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onClose}
                            className="min-h-[44px] min-w-[44px]"
                            aria-label="Close discount dialog"
                        >
                            ✕
                        </Button>
                    </div>

                    {/* ── Pending body ────────────────────────────────── */}
                    <div className="flex-1 overflow-y-auto px-5 pb-5 space-y-4">
                        <div className="rounded-xl px-4 py-4 bg-gradient-to-r from-warning-50 to-amber-50 dark:from-warning-900/20 dark:to-amber-900/20">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-warning-500 to-amber-500 flex items-center justify-center text-white shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <polyline points="12 6 12 12 16 14" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-warning-800 dark:text-warning-300">
                                        Awaiting approval
                                    </p>
                                    <p className="text-xs text-warning-600 dark:text-warning-400 animate-pulse">
                                        {pendingLabel} discount — "{pendingApproval.reason}"
                                    </p>
                                </div>
                            </div>

                            {/* Preview strip */}
                            <div className="space-y-1 mt-3 pt-3 border-t border-warning-200/60 dark:border-warning-700/40">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-warning-700 dark:text-warning-400">After line discounts</span>
                                    <span className="tabular-nums font-semibold text-warning-800 dark:text-warning-200">
                                        {fmt(currentSubtotal)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-warning-700 dark:text-warning-400">Discount (pending)</span>
                                    <span className="tabular-nums font-semibold text-warning-600 dark:text-warning-300 animate-pulse">
                                        − {fmt(pendingPreviewAmount)}
                                    </span>
                                </div>
                                <div className="h-px bg-warning-200/70 dark:bg-warning-700/50 my-1" />
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold text-warning-800 dark:text-warning-200">
                                        After discount
                                    </span>
                                    <span className="tabular-nums text-base font-bold text-warning-700 dark:text-warning-300">
                                        {fmt(Math.max(0, currentSubtotal - pendingPreviewAmount))}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Footer ──────────────────────────────────────── */}
                    <div className="px-5 py-4 flex items-center justify-end">
                        <Button
                            variant="ghost"
                            size="md"
                            onClick={onClose}
                            className="min-h-[44px]"
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Normal / Request Approval State ───────────────────────────────────

    return (
        <div
            className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in p-0 sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bill-discount-title"
        >
            <div
                ref={panelRef}
                className={cn(
                    GLASS,
                    'w-full sm:w-[560px] max-h-[92vh] flex flex-col animate-slide-up',
                )}
            >
                {/* ── Header (brand → indigo gradient) ─────────────────── */}
                <div className="px-5 py-4 flex items-start justify-between">
                    <div>
                        <h2
                            id="bill-discount-title"
                            className="text-lg font-semibold bg-gradient-to-r from-brand-600 to-indigo-600 bg-clip-text text-transparent"
                        >
                            Apply Bill Discount
                        </h2>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                            Pro-rated across line items — GST recomputed per line
                        </p>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="min-h-[44px] min-w-[44px]"
                        aria-label="Close discount dialog"
                    >
                        ✕
                    </Button>
                </div>

                {/* ── Body ─────────────────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto px-5 pb-5 space-y-4">
                    {permission.kind === 'platform' && (
                        <Alert
                            variant="warning"
                            title="Platform role"
                            description="Bill discounts are tenant-staff only. Please switch to an Owner, Manager, or Cashier account to operate the POS."
                        />
                    )}
                    {permission.kind === 'denied' && (
                        <Alert
                            variant="danger"
                            title="Not permitted"
                            description="Your role cannot apply bill discounts. Ask a Manager or Owner."
                        />
                    )}

                    {roleIsPermitted && (
                        <>
                            {onValidateVoucher && (
                                <div className="rounded-xl border border-violet-200 dark:border-violet-700/60 bg-gradient-to-r from-violet-50 to-pink-50 dark:from-violet-900/20 dark:to-pink-900/20 p-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label className="text-xs font-semibold text-violet-700 dark:text-violet-300 uppercase tracking-wide">
                                            Voucher Code
                                        </label>
                                        {voucherStatus.kind === 'valid' && (
                                            <button
                                                type="button"
                                                onClick={handleClearVoucher}
                                                className="text-[11px] text-violet-700 dark:text-violet-300 underline hover:no-underline"
                                            >
                                                Remove voucher
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex items-stretch gap-2">
                                        <div className="flex-1">
                                            <Input
                                                value={voucherCode}
                                                onChange={(v) => {
                                                    setVoucherCode(v.toUpperCase());
                                                    if (voucherStatus.kind !== 'idle') setVoucherStatus({ kind: 'idle' });
                                                }}
                                                placeholder="e.g. FREEDESSERT"
                                                disabled={voucherStatus.kind === 'validating' || voucherStatus.kind === 'valid'}
                                            />
                                        </div>
                                        <Button
                                            variant="secondary"
                                            onClick={() => setScannerOpen(true)}
                                            disabled={
                                                voucherStatus.kind === 'validating' ||
                                                voucherStatus.kind === 'valid'
                                            }
                                            aria-label="Scan voucher QR or barcode"
                                            className="shrink-0"
                                        >
                                            <span className="inline-flex items-center gap-1.5">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <path d="M3 7V5a2 2 0 0 1 2-2h2" />
                                                    <path d="M17 3h2a2 2 0 0 1 2 2v2" />
                                                    <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
                                                    <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
                                                    <line x1="3" y1="12" x2="21" y2="12" />
                                                </svg>
                                                Scan
                                            </span>
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={() => handleValidateVoucher()}
                                            disabled={
                                                !voucherCode.trim() ||
                                                voucherStatus.kind === 'validating' ||
                                                voucherStatus.kind === 'valid'
                                            }
                                            className="!bg-gradient-to-r !from-violet-500 !to-pink-500 !text-white hover:!from-violet-600 hover:!to-pink-600 shrink-0"
                                        >
                                            {voucherStatus.kind === 'validating' ? 'Checking…' : 'Apply Voucher'}
                                        </Button>
                                    </div>
                                    {voucherStatus.kind === 'valid' && (
                                        <p className="text-[11px] font-semibold text-success-700 dark:text-success-400">
                                            ✓ {voucherStatus.message}
                                        </p>
                                    )}
                                    {voucherStatus.kind === 'error' && (
                                        <p className="text-[11px] font-semibold text-danger-700 dark:text-danger-400">
                                            ✕ {voucherStatus.message}
                                        </p>
                                    )}
                                </div>
                            )}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wide">
                                        Discount
                                    </label>
                                    <span className="text-[11px] text-neutral-500 dark:text-neutral-400 tabular-nums">
                                        {capLabel === null ? 'No limit' : `Max ${capLabel}`} for {normalizedRole || 'GUEST'}
                                    </span>
                                </div>
                                <DiscountInput
                                    value={rawValue}
                                    discountType={atomType}
                                    currency={currency}
                                    onValueChange={(v) => {
                                        // Live input — clamp percent to 100, but DON'T touch
                                        // committedValue. The preview only refreshes when the
                                        // type toggle changes or the inline Apply is clicked.
                                        const next = atomType === 'percent' ? Math.min(v, 100) : v;
                                        setRawValue(next);
                                    }}
                                    onTypeChange={(t) => {
                                        // Toggling amount ↔ % re-runs the calculation against the
                                        // current input value under the new mode.
                                        setAtomType(t);
                                        const next = t === 'percent' ? Math.min(rawValue, 100) : rawValue;
                                        setRawValue(next);
                                        setCommittedValue(next);
                                    }}
                                    // Inline Apply commits the typed value into the preview
                                    // (Subtotal / Discount / After discount strip + footer
                                    // Apply enable state). Footer "Apply discount" is what
                                    // actually writes to the bill.
                                    onApply={() => {
                                        const next = atomType === 'percent' ? Math.min(rawValue, 100) : rawValue;
                                        if (next !== rawValue) setRawValue(next);
                                        setCommittedValue(next);
                                    }}
                                />
                                {overCap && (
                                    <div className="rounded-xl px-4 py-3 bg-gradient-to-r from-warning-50 to-amber-50 dark:from-warning-900/20 dark:to-amber-900/20">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-warning-500 to-amber-500 flex items-center justify-center text-white shrink-0">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                                </svg>
                                            </div>
                                            <p className="text-xs text-warning-800 dark:text-warning-300">
                                                This exceeds your <span className="font-bold tabular-nums">{capLabel}</span> cap.
                                                {onRequestApproval
                                                    ? ' A Manager or Owner must approve it.'
                                                    : ' Reduce the value to apply.'}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div
                                className="space-y-1"
                                onKeyDownCapture={(e) => {
                                    // Enter (without Shift) submits when valid; Shift+Enter
                                    // inserts a newline inside the TextArea as usual.
                                    if (
                                        e.key === 'Enter' &&
                                        !e.shiftKey &&
                                        (e.target as HTMLElement).tagName === 'TEXTAREA'
                                    ) {
                                        if (canApply) {
                                            e.preventDefault();
                                            handleApply();
                                        } else if (canRequestApproval) {
                                            e.preventDefault();
                                            handleRequestApproval();
                                        }
                                    }
                                }}
                            >
                                <label
                                    htmlFor="bill-discount-reason"
                                    className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wide"
                                >
                                    Reason (required for audit)
                                </label>
                                <TextArea
                                    id="bill-discount-reason"
                                    value={reason}
                                    onChange={setReason}
                                    placeholder="e.g. Loyal customer, festive offer, manager override"
                                    rows={3}
                                    maxLength={500}
                                    resize="none"
                                    required
                                    error={
                                        reason.length > 0 && !reasonValid
                                            ? 'Reason must be at least 3 characters.'
                                            : undefined
                                    }
                                />
                            </div>

                            {/* Preview strip */}
                            <div className="rounded-xl px-4 py-3 bg-gradient-to-r from-brand-50 to-indigo-50 dark:from-brand-900/20 dark:to-indigo-900/20">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-neutral-600 dark:text-neutral-400">After line discounts</span>
                                    <span className="tabular-nums font-semibold text-neutral-800 dark:text-neutral-100">
                                        {fmt(currentSubtotal)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-xs mt-1">
                                    <span className="text-neutral-600 dark:text-neutral-400">Discount</span>
                                    <span className="tabular-nums font-semibold text-warning-600 dark:text-warning-400">
                                        − {fmt(previewDiscountAmount)}
                                    </span>
                                </div>
                                <div className="h-px bg-neutral-200/70 dark:bg-neutral-700/70 my-2" />
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                                        After discount
                                    </span>
                                    <span className="tabular-nums text-base font-bold bg-gradient-to-r from-brand-600 to-indigo-600 bg-clip-text text-transparent">
                                        {fmt(previewFinal)}
                                    </span>
                                </div>
                            </div>

                            {/* FR-BILL-043: P&L unprofitability warning */}
                            {unprofitableLines.length > 0 && (
                                <div
                                    className="rounded-xl px-4 py-3 bg-gradient-to-r from-danger-50 to-rose-50 dark:from-danger-900/20 dark:to-rose-900/20"
                                    role="alert"
                                    aria-live="polite"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-danger-500 to-rose-500 flex items-center justify-center text-white shrink-0 mt-0.5">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                                                <path d="M12 9v4" />
                                                <path d="M12 17h.01" />
                                            </svg>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-semibold text-danger-800 dark:text-danger-300">
                                                {unprofitableLines.length === 1
                                                    ? '1 item will be sold below cost'
                                                    : `${unprofitableLines.length} items will be sold below cost`}
                                            </p>
                                            <ul className="mt-1.5 space-y-1">
                                                {unprofitableLines.slice(0, 3).map((line) => (
                                                    <li key={line.sku} className="text-[11px] text-danger-700 dark:text-danger-400 tabular-nums">
                                                        <span className="font-medium">{line.name}</span>
                                                        <span className="text-danger-500 dark:text-danger-500 ml-1">
                                                            Loss: {fmt(line.lossAmount)}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                            {unprofitableLines.length > 3 && (
                                                <p className="text-[11px] text-danger-600 dark:text-danger-400 mt-1">
                                                    +{unprofitableLines.length - 3} more {unprofitableLines.length - 3 === 1 ? 'item' : 'items'} below cost
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* ── Footer ───────────────────────────────────────────── */}
                <div className="px-5 py-4 flex items-center justify-between gap-2">
                    {currentDiscount ? (
                        <Button
                            variant="danger"
                            size="md"
                            onClick={onClear}
                            className="min-h-[44px]"
                            aria-label="Remove bill discount"
                        >
                            Remove discount
                        </Button>
                    ) : (
                        <span />
                    )}
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="md"
                            onClick={onClose}
                            className="min-h-[44px]"
                        >
                            Cancel
                        </Button>
                        {canSubmitRequestApproval ? (
                            <Button
                                variant="secondary"
                                size="md"
                                onClick={handleRequestApproval}
                                disabled={isRequesting}
                                className="min-h-[44px] !bg-gradient-to-r !from-warning-500 !to-amber-500 !text-white hover:!from-warning-600 hover:!to-amber-600"
                                aria-label={confirmAriaLabel}
                            >
                                {isRequesting ? 'Requesting…' : 'Request Approval'}
                            </Button>
                        ) : (
                            <Button
                                variant="primary"
                                size="md"
                                onClick={handleApply}
                                disabled={!canSubmit}
                                className="min-h-[44px]"
                                aria-label={confirmAriaLabel}
                            >
                                Apply discount
                            </Button>
                        )}
                    </div>
                </div>

                {/* Voucher QR/barcode scanner — nested here so it layers above
                    this modal's own overlay (relative z establishes the context). */}
                {scannerOpen && (
                    <div className="relative z-[95]">
                        <Suspense fallback={null}>
                            <CameraBarcodeScanner
                                open={scannerOpen}
                                onScan={handleScanVoucher}
                                onClose={() => setScannerOpen(false)}
                            />
                        </Suspense>
                    </div>
                )}
            </div>
        </div>
    );
}
